import React from "react";
import * as Sentry from "@sentry/react";
import { environmentState } from "../store/layout";
import { useRecoilValue } from "recoil";
import { EnvironmentEnum } from "@lyfegen-platform/graphql-schema/models";
import { Mutation, Query } from "../models/generated/graphql";

export enum SentryErrorLevels {
	Error = "error",
	Warning = "warning",
	Debug = "debug",
	Info = "info",
	Fatal = "fatal",
}

export enum SentryStatusCodes {
	BadRequest = 400,
	Unauthorized = 401,
	Forbidden = 403,
	NotFound = 404,
	InternalServerError = 500,
	BadGateway = 502,
	ServiceUnavailable = 503,
	GatewayTimeout = 504,
	TimeoutWithRetry = 524,
}

export interface SentryLogParams {
	errorLog: any;
	statusCode?: SentryStatusCodes | string;
	userId?: string;
	level: SentryErrorLevels;
	operation?: keyof Mutation | keyof Query;
	variables?: object | null;
}

interface LogToSentryFunctionArgs extends SentryLogParams {
	env: EnvironmentEnum;
}

const internalIdPattern = /^[A-Z]{2}-\d{10}$/;

const extractInternalIds = (value: object | null | undefined, visited: Set<unknown>): Array<string> => {
	if (!value || typeof value !== "object" || visited.has(value)) {
		return [];
	}

	visited.add(value);
	if (Array.isArray(value)) {
		return value.flatMap((item) => extractInternalIds(item, visited));
	}

	return [
		...Object.entries(value)
			.filter(
				([key, innerValue]) =>
					key.toLowerCase().includes("id") && typeof innerValue === "string" && internalIdPattern.test(innerValue)
			)
			.map(([_, value]) => value as string),
		...Object.values(value)
			.filter((innerValue) => innerValue && typeof innerValue === "object")
			.flatMap((innerValue) => extractInternalIds(innerValue, visited)),
	];
};

export const logToSentryFunction = (args: LogToSentryFunctionArgs) => {
	const { env, errorLog, level, statusCode, userId, operation, variables } = args;
	const errorTitle = level === SentryErrorLevels.Warning ? `Maintenance Page ${statusCode}` : "Banner Error";
	const internalIds = extractInternalIds(variables, new Set<unknown>()).join(",");

	Sentry.withScope((scope) => {
		scope.setLevel(level);
		scope.setExtra("errorLog", errorLog);
		scope.setTag("environment", env);
		scope.setExtra("env", env);
		scope.setExtra("isCore", false);
		scope.setTag("env", env);
		scope.setTag("internalIds", internalIds);
		scope.setTag("statusCode", statusCode?.toString());
		scope.setUser({ name: "User", id: userId });
		operation && scope.setTag("operation", operation);
		Sentry.captureException(new Error(errorTitle), scope);
	});
};

export const getNetworkErrorStatusCode = (networkError: Error | null | undefined): number | undefined => {
	if (!networkError) return undefined;
	if (typeof networkError === "object" && "statusCode" in networkError) {
		return (networkError as { statusCode: number }).statusCode;
	}

	return undefined;
};

const LogToSentry: React.FC<SentryLogParams> = ({ errorLog, statusCode, userId, level, operation, variables }: SentryLogParams) => {
	const env = useRecoilValue(environmentState);
	logToSentryFunction({ errorLog, statusCode, userId, level, env: env as EnvironmentEnum, operation, variables });

	return null;
};

export default LogToSentry;
